import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "~/hook/use-mobile";
import { useFontStyle } from "~/hook/useFontStyle";
import useLocale from "~/hook/useLocale";
import { setIsDialogOpen } from "~/lib/store/slice/loginSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { Facebook, Instagram, Whatsapp, X, Youtube } from "~/svg/footerIcons";
import OurApps from "../AppsInfo/OurApps";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import ChangeLanguage from "./ChangeLanguage";
import ProfileComponent from "./ProfileComponent";

interface footerDataType {
  name: string;
  is_visible: boolean;
  footer_links: { name: string; url: string }[];
}
interface FooterProps {
  footerData: footerDataType[];
}

const socialLinks = [
  { icon: <Youtube />, href: "#", label: "YouTube Channels", isYoutube: true },
  { icon: <Facebook />, href: "https://www.facebook.com/swaminarayanbhagwan2/", label: "Facebook" },
  { icon: <Instagram />, href: "https://www.instagram.com/swaminarayanbhagwan_/", label: "Instagram" },
  { icon: <X />, href: "https://x.com/Swaminarayanbh3?ref_src=twsrc%5Etfw", label: "Twitter" },
  {
    icon: <Whatsapp />,
    href: "https://api.whatsapp.com/send?phone=919601290051&text=hi%2c%20%0aName%3a%20your%20name%2c%20%0aCity%3a%20your%20city%2c%20%0aGender%3a%20M%2fF&source=&data=&app_absent=",
    label: "WhatsApp",
  },
];

const youtubeChannels = [
  {
    name: "Swaminarayan Bhagwan",
    url: "https://www.youtube.com/@swaminarayanbhagwan1",
    image: "/images/swaminarayan_bhagawan_yt_chanel.jpg",
  },
  {
    name: "Swaminarayan",
    url: "https://www.youtube.com/@swaminarayan",
    image: "/images/swaminarayan_yt_chanel.jpg",
  },
  {
    name: "Swaminarayan Kirtan",
    url: "https://www.youtube.com/@swaminarayankirtanlive",
    image: "/images/swaminarayan_kirtan_yt_chanel.jpg",
  },
];

const policyLinks = [
  { to: "/policy/cookie-policy", label: "Cookie Policy" },
  { to: "/policy/disclaimer", label: "Disclaimer" },
  { to: "/policy/terms-condition", label: "Terms & Condition" },
  { to: "/policy/privacy-policy", label: "Privacy Policy" },
];

function Footer({ footerData }: FooterProps) {
  const dispatch = useAppDispatch();
  const { LocalizedLink } = useLocale();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { regular, mediumInter, medium, normalInter } = useFontStyle();
  const [isYoutubeDialogOpen, setIsYoutubeDialogOpen] = useState(false);

  const { isUserLogin } = useAppSelector((state) => state.login);

  const footerDataFirstIndex = (footerData && footerData[0]) || {};

  const handleLogin = () => {
    dispatch(setIsDialogOpen());
  };

  const FooterLinks = () => {
    return (
      <>
        {footerDataFirstIndex ? (
          <div className="footer-section space-y-2 w-fit md:w-auto">
            {footerDataFirstIndex.is_visible && <p className={cn("body_text_m", regular)}>{footerDataFirstIndex.name}</p>}
            <div className={cn("gap-x-8 gap-y-0.5 grid", (footerDataFirstIndex?.footer_links?.length ?? 0) > 7 ? "grid-cols-2" : "grid-cols-1")}>
              {footerDataFirstIndex?.footer_links?.map((link, linkIndex) => (
                <LocalizedLink
                  key={linkIndex}
                  to={link.url}
                  className={cn(
                    "block text-white/70 hover:text-white transition-colors body_text_s overflow-hidden text-nowrap truncate",
                    mediumInter,
                  )}
                >
                  {link.name}
                </LocalizedLink>
              ))}
            </div>
          </div>
        ) : null}
      </>
    );
  };

  return (
    <footer className="body-container text-white pt-8 pb-4">
      <div className="max-w-7xl mx-auto">
        {/* Mobile Layout */}
        <div className="block px-3 lg:hidden">
          {/* Logo and Temple Info */}
          <div className="text-center mb-8 border-b border-amber-800">
            <div className="relative inline-block mb-4">
              <div className="w-24 h-24 rounded-full flex items-start justify-center mx-auto relative">
                <img src="/svg/footer.svg" alt="Maharaj Shadow" className="absolute inset-0 w-auto h-auto z-[1] select-none" />
                <div className="w-20 h-20 absolute inset-2 bg-white rounded-full">
                  <img
                    src="/images/maharaj-header-icon.webp"
                    alt="Swaminarayan Temple"
                    className="w-full h-full rounded-full object-cover z-0 select-none"
                  />
                </div>
              </div>
            </div>
            <p className={cn("h2_heading text-white font-normal ", medium)} suppressHydrationWarning>
              {t("footer.title")}
            </p>
            <p className={cn("text-white body_text_m font-inter font-normal mb-6", normalInter)} suppressHydrationWarning>
              {t("footer.sub_title")}
            </p>

            {/* Social Icons */}
            <div className="flex justify-center space-x-3 mb-8">
              {socialLinks.map(({ icon, href, label, isYoutube }) => (
                <button
                  key={label}
                  onClick={(e) => {
                    if (isYoutube) {
                      e.preventDefault();
                      setIsYoutubeDialogOpen(true);
                    } else {
                      window.open(href, "_blank", "noopener,noreferrer");
                    }
                  }}
                  aria-label={label}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-amber-900 transition-colors hover:bg-amber-100"
                  title={label}
                >
                  {icon}
                </button>
              ))}
            </div>

            <div className="flex  flex-col-reverse items-center gap-3 justify-center mb-8">
              <div className="flex flex-col gap-1">
                {/* <span className="body_text_s invisible"> {t("login_page.login", "Login")}</span> */}
                {!isUserLogin ? (
                  <button onClick={handleLogin} className={cn("footer-login-btn", normalInter)}>
                    {t("login_page.login", "Login")}
                  </button>
                ) : (
                  <ProfileComponent isFooter={true} />
                )}
              </div>

              <div className="flex flex-col items-center gap-1">
                <span className={cn("text-sm", normalInter)} suppressHydrationWarning>
                  {t("footer.language")}
                </span>
                <ChangeLanguage variant="footer" isOptionLang={false} />
              </div>
            </div>
          </div>

          {/* Our Apps */}
          <div className="mb-8 border-b border-amber-800 pb-10 space-y-2">
            <p className={cn("text-16 lg:text-lg text-center", regular)} suppressHydrationWarning>
              {t("footer.ourApps")}
            </p>
            <OurApps width={64} height={64} textColor="text-white/70" textSize={12} className="flex justify-center items-center px-10" />
          </div>

          {/* Quick Links */}
          <div className="mb-8">
            <FooterLinks />
          </div>

          {/* Language Selection */}
          {/* <div className="mb-8">
            <LanguageDropdown variant="footer" className="max-w-[10rem]" />
          </div> */}
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:block">
          <div className="w-full grid grid-cols-3 gap-2 justify-center items-center md:px-3 ">
            {/* Our Apps */}
            <div className="w-full flex flex-col justify-start items-center gap-2">
              <div>
                <p className={cn("body_text_l font-normal mb-2", regular)} suppressHydrationWarning>
                  {t("footer.ourApps")}
                </p>
                <OurApps width={64} height={64} textColor="text-white/70" textSize={12} className="!grid-cols-2" />
              </div>
            </div>

            {/* Center - Logo and Temple Info */}
            <div className="w-full text-center border-r border-l border-amber-800">
              <div className="relative inline-block mb-4">
                <div className="w-32 h-32 rounded-full flex items-center justify-center mx-auto relative">
                  <img src="/svg/footer.svg" alt="Maharaj Shadow" className="absolute inset-0 w-auto h-auto select-none " />
                  <div className="w-24 h-24 absolute inset-4 bg-white rounded-full">
                    <img
                      src="/images/maharaj-header-icon.webp"
                      alt="Swaminarayan Temple"
                      className="w-full h-full rounded-full object-cover z-0 select-none"
                    />
                  </div>
                </div>
              </div>
              <p className={cn("body_text_l font-normal", medium)} suppressHydrationWarning>
                {t("footer.title")}
              </p>
              <p className={cn("text-white body_text_s font-inter font-normal mb-6", normalInter)} suppressHydrationWarning>
                {t("footer.sub_title")}
              </p>

              {/* Social Icons */}
              <div className="flex justify-center space-x-4 mb-4 ">
                {socialLinks.map(({ icon, href, label, isYoutube }) => (
                  <button
                    key={label}
                    onClick={(e) => {
                      if (isYoutube) {
                        e.preventDefault();
                        setIsYoutubeDialogOpen(true);
                      } else {
                        window.open(href, "_blank", "noopener,noreferrer");
                      }
                    }}
                    aria-label={label}
                    className="w-auto h-auto hover:opacity-80 transition-opacity"
                  >
                    {icon}
                  </button>
                ))}
              </div>
              <div className="flex justify-center items-center gap-5">
                <div className="flex flex-col gap-1">
                  <span className={cn("body_text_s", normalInter)} suppressHydrationWarning>
                    {t("footer.language")}
                  </span>
                  <ChangeLanguage variant="footer" />
                </div>
              </div>
            </div>

            {/* Right side - Quick Links and Language */}
            <div className="w-full flex flex-col items-center gap-6 px-10">
              <div className="flex flex-col gap-1">
                <span className={cn("body_text_s invisible", normalInter)}> {t("login_page.login", "Login")}</span>
                {!isUserLogin ? (
                  <button onClick={handleLogin} className={cn("footer-login-btn", normalInter)}>
                    {t("login_page.login", "Login")}
                  </button>
                ) : (
                  <ProfileComponent isFooter={true} />
                )}
              </div>

              <FooterLinks />
              {/* <LanguageDropdown
                variant="footer"
                className={cn("max-w-[16rem] self-center", lang == LANG.gujarati ? "max-w-[11rem]" : "max-w-[16rem]")}
              /> */}
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="md:space-y-8 px-3 md:mt-10">
          {/* divider */}
          <div className="border-t border-amber-800" />

          <div className="flex flex-col flex-wrap">
            {footerData?.slice(1).map((section, sectionIdx) => (
              <div key={sectionIdx} className="flex flex-col mt-5">
                {section.is_visible && <p className={cn("body_text_m capitalize", regular)}>{section.name}</p>}
                <div className="flex flex-wrap">
                  {section.footer_links.map((link, linkIdx) => (
                    <span key={linkIdx} className="flex items-center text-white/70">
                      <LocalizedLink
                        to={link.url}
                        className={cn("text-white/70 font-inter body_text_s font-normal capitalize hover:text-white transition-colors", normalInter)}
                      >
                        {link.name}
                      </LocalizedLink>
                      {linkIdx < section.footer_links.length - 1 && <span className="mx-2 text-white/70">|</span>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div
            className={cn(
              "md:mt-6 pt-4 border-t border-amber-800 font-inter flex flex-col lg:flex-row justify-between items-center body_text_xs text-white/70",
              normalInter,
            )}
          >
            <p>© Shree Swaminarayan Temple, {dayjs().year()}. All Rights Reserved</p>
            <div className="flex flex-wrap mt-2 gap-x-1 md:gap-x-2 lg:mt-0">
              {policyLinks?.map((link, index) => (
                <div key={link.to} className="flex gap-x-1 md:gap-x-2">
                  <LocalizedLink to={link.to} className={cn("hover:text-white/70 transition-colors body_text_sx ", normalInter)}>
                    {link.label}
                  </LocalizedLink>
                  <div>{index !== policyLinks.length - 1 && <span className="text-white/70">•</span>}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* YouTube Channels Dialog */}
      <Dialog open={isYoutubeDialogOpen} onOpenChange={setIsYoutubeDialogOpen}>
        <DialogContent className="max-w-[95%] sm:max-w-md bg-white rounded-lg">
          <DialogHeader>
            <DialogTitle className={cn("text-center text-textHeadingColor body_text_l", medium)}>
              {t("footer.youtubeChannels", "Our YouTube Channels")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {youtubeChannels.map((channel) => (
              <a
                key={channel.name}
                href={channel.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-3 rounded-lg border border-borderPrimary hover:bg-gray-50 transition-colors group"
                onClick={() => setIsYoutubeDialogOpen(false)}
              >
                <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0 border-2 border-amber-900">
                  <img src={channel.image} alt={channel.name} className="w-full h-full object-cover select-none" />
                </div>
                <div className="flex-1">
                  <h3 className={cn("body_text_m text-textHeadingColor group-hover:text-buttonColor transition-colors", mediumInter)}>
                    {channel.name}
                  </h3>
                  <p className={cn("body_text_xs text-textSecondaryColor", normalInter)} suppressHydrationWarning>
                    {t("footer.visitChannel", "Visit Channel")}
                  </p>
                </div>
                <Youtube />
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </footer>
  );
}

export default Footer;
